<template>
    <Dialog
        v-model:visible="modalState.opened"
        append-to="#app"
        class="tl-prime-dialog tl-prime-dialog__size-s"
        modal
        :draggable="false"
        :dismissable-mask="true"
        :data-testid="testId"
    >
        <template #header>
            <CardHeader
                :title="title"
                :description="description"
                data-testid="dialog-title"
            />
        </template>

        <div
            v-if="withReason"
            class="mt-6"
        >
            <Textarea
                v-model="reason"
                rows="2"
                auto-resize
                placeholder="Reason"
                fluid
                size="small"
            />
        </div>

        <div
            v-if="withConfirmationInput"
            class="mt-6"
        >
            <label class="block mb-2 text-sm">Type <strong>{{ withConfirmationInput }}</strong> to confirm</label>
            <InputText
                v-model="confirmationInput"
                fluid
                size="small"
                placeholder="Type to confirm"
            />
        </div>

        <template #footer>
            <CardFooter>
                <Button
                    size="md"
                    variant="outline"
                    :label="t('common.cancel')"
                    :data-testid="`${testId}-cancel`"
                    @click="cancel"
                />
                <Button
                    size="md"
                    variant="primary"
                    destructive
                    :label="t('common.confirm')"
                    :loading="isLoading"
                    :disabled="!!withConfirmationInput && confirmationInput !== withConfirmationInput"
                    :data-testid="`${testId}-confirm`"
                    v-bind="confirmButtonProps"
                    @click="submit"
                />
            </CardFooter>
        </template>
    </Dialog>
</template>

<script setup lang="ts">
import useError from '@/composables/error/useError'
import { useI18n } from '@/composables/i18n'
import useModal, { type ConfirmModalProps } from '@/composables/modal'
import { ref } from 'vue'
import Button from '@/components/atoms/Button/Button.vue'
import CardHeader from '@/components/atoms/Card/CardHeader.vue'
import CardFooter from '@/components/atoms/Card/CardFooter.vue'

const { modalState } = useModal()

const props = defineProps<ConfirmModalProps>()

const { t } = useI18n()

const emit = defineEmits<{
    (e: 'success', reason?: string): void
    (e: 'cancel'): void
}>()

const { handleApiError } = useError()

const reason = ref('')
const confirmationInput = ref('')

const cancel = () => {
    emit('cancel')
}

const { call, isLoading } = props.apiCallInstance || {}

const submit = async () => {
    try {
        if (props.apiCallInstance && typeof call === 'function') {
            await call()
        }
        emit('success', props.withReason ? reason.value : undefined)
    } catch (e: unknown) {
        handleApiError(e)
    }
}
</script>
